import { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'dark' | 'light';

const ThemeContext = createContext<{ theme: Theme; toggle: () => void }>({
  theme: 'dark',
  toggle: () => {},
});

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // Always start from the SSR default ('dark') so the server-rendered HTML and
  // the first client render match exactly — otherwise the theme toggle icon
  // (Sun vs Moon) differs and React throws a hydration mismatch. The persisted
  // preference (or the visitor's OS colour-scheme) is applied right after mount.
  const [theme, setTheme] = useState<Theme>('dark');
  // Track whether the user has explicitly chosen a theme. When they haven't,
  // we follow the OS `prefers-color-scheme` and update live if the OS toggles.
  const [userOverride, setUserOverride] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem('db-theme') as Theme | null;
    if (stored === 'light' || stored === 'dark') {
      setUserOverride(true);
      if (stored !== theme) setTheme(stored);
      return;
    }
    // No stored override — follow the visitor's system preference.
    const mql = window.matchMedia('(prefers-color-scheme: light)');
    const sysTheme: Theme = mql.matches ? 'light' : 'dark';
    if (sysTheme !== theme) setTheme(sysTheme);
    const onChange = (e: MediaQueryListEvent) => {
      // Only track the OS if the user hasn't toggled during this session.
      if (!localStorage.getItem('db-theme')) setTheme(e.matches ? 'light' : 'dark');
    };
    mql.addEventListener?.('change', onChange);
    return () => mql.removeEventListener?.('change', onChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', theme);
    // Keep the <html> element's own inline background in sync with the theme.
    // The SSR shell hard-codes a dark navy inline style for the first paint;
    // without this, light-theme users keep a dark navy <html> background behind
    // the content, which shows through as a grey→white flash on overlays
    // (e.g. the mobile chat view).
    root.style.backgroundColor = theme === 'light' ? 'hsl(240 30% 98%)' : 'hsl(228 45% 9%)';
    root.style.colorScheme = theme === 'light' ? 'light' : 'dark';
    // Only persist once the user has explicitly toggled — otherwise we'd lock
    // in the initial OS theme and stop tracking future OS changes.
    if (userOverride) localStorage.setItem('db-theme', theme);
  }, [theme, userOverride]);

  const toggle = () => {
    setUserOverride(true);
    setTheme((t) => (t === 'dark' ? 'light' : 'dark'));
  };

  return (
    <ThemeContext.Provider value={{ theme, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
